import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail, MessageCircle, Sun, TrendingUp, Send, RefreshCw, Users, Zap } from 'lucide-react';

const gold = '#C9A961';

export default function AdminCampaigns({ adminToken }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testEmail, setTestEmail] = useState('');
  const [testType, setTestType] = useState('daily_insight');
  const [testDay, setTestDay] = useState(1);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');

  const headers = { Authorization: `Bearer ${adminToken}` };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/blog/admin/campaigns/stats', { headers });
      setStats(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchStats(); }, []);

  const sendTest = async () => {
    if (!testEmail) return;
    setSending(true); setMsg('');
    try {
      const res = await axios.post('/api/blog/admin/campaigns/send-test', { type: testType, email: testEmail, day: testDay }, { headers });
      setMsg(res.data.message);
    } catch (e) { setMsg(e.response?.data?.detail || 'Failed'); }
    finally { setSending(false); }
  };

  const triggerCampaign = async (type) => {
    setMsg(`Running ${type}...`);
    try {
      const res = await axios.post('/api/blog/admin/campaigns/trigger', { type }, { headers });
      setMsg(res.data.message);
      fetchStats();
    } catch (e) { setMsg(e.response?.data?.detail || 'Failed'); }
  };

  const cardStyle = { background: '#101632', borderRadius: '10px', padding: '1rem', border: '1px solid rgba(255,255,255,0.06)', textAlign: 'center' };

  if (loading) return <p style={{ color: '#9CA3AF' }}>Loading campaigns...</p>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
        <h2 style={{ color: '#FFF', fontSize: '1.3rem' }}>Campaign Manager</h2>
        <button onClick={fetchStats} style={{ background: 'rgba(201,169,97,0.15)', color: gold, padding: '0.4rem 0.8rem', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '0.8rem' }}><RefreshCw size={12} style={{ display: 'inline', marginRight: 4 }} />Refresh</button>
      </div>

      {msg && <p style={{ color: msg.includes('Failed') ? '#EF4444' : '#10B981', fontSize: '0.85rem', padding: '0.5rem 0.75rem', background: 'rgba(16,185,129,0.08)', borderRadius: '6px', marginBottom: '1rem' }}>{msg}</p>}

      {stats && (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {/* User Overview */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
            <div style={cardStyle}>
              <Users size={20} color="#3B82F6" style={{ marginBottom: '0.3rem' }} />
              <p style={{ color: '#6B7280', fontSize: '0.72rem' }}>Total Users</p>
              <p style={{ color: '#3B82F6', fontSize: '1.5rem', fontWeight: 700 }}>{stats.users.total}</p>
            </div>
            <div style={cardStyle}>
              <Zap size={20} color="#10B981" style={{ marginBottom: '0.3rem' }} />
              <p style={{ color: '#6B7280', fontSize: '0.72rem' }}>Paying Users</p>
              <p style={{ color: '#10B981', fontSize: '1.5rem', fontWeight: 700 }}>{stats.users.paying}</p>
            </div>
            <div style={cardStyle}>
              <TrendingUp size={20} color="#F59E0B" style={{ marginBottom: '0.3rem' }} />
              <p style={{ color: '#6B7280', fontSize: '0.72rem' }}>Non-Paying</p>
              <p style={{ color: '#F59E0B', fontSize: '1.5rem', fontWeight: 700 }}>{stats.users.non_paying}</p>
            </div>
          </div>

          {/* Campaign Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            {/* Daily Insight */}
            <div style={{ ...cardStyle, textAlign: 'left', border: '1px solid rgba(245,158,11,0.2)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Sun size={18} color="#F59E0B" />
                  <span style={{ color: '#FDE68A', fontWeight: 700, fontSize: '0.88rem' }}>Daily Morning Insight</span>
                </div>
                <button onClick={() => triggerCampaign('daily_insight')} style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B', padding: '0.3rem 0.6rem', borderRadius: '4px', border: 'none', cursor: 'pointer', fontSize: '0.72rem' }}>Run Now</button>
              </div>
              <p style={{ color: '#9CA3AF', fontSize: '0.78rem' }}>6 AM IST daily to paying users</p>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                <span style={{ color: '#F59E0B', fontSize: '0.82rem' }}><strong>{stats.daily_insight.total_sent}</strong> total</span>
                <span style={{ color: '#6B7280', fontSize: '0.82rem' }}>{stats.daily_insight.today} today</span>
              </div>
            </div>

            {/* Onboarding Drip */}
            <div style={{ ...cardStyle, textAlign: 'left', border: '1px solid rgba(59,130,246,0.2)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Mail size={18} color="#3B82F6" />
                  <span style={{ color: '#93C5FD', fontWeight: 700, fontSize: '0.88rem' }}>Onboarding Drip</span>
                </div>
                <button onClick={() => triggerCampaign('onboarding')} style={{ background: 'rgba(59,130,246,0.15)', color: '#3B82F6', padding: '0.3rem 0.6rem', borderRadius: '4px', border: 'none', cursor: 'pointer', fontSize: '0.72rem' }}>Run Now</button>
              </div>
              <p style={{ color: '#9CA3AF', fontSize: '0.78rem' }}>Day 1, 3, 5, 7, 14, 30 — paying users</p>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                <span style={{ color: '#3B82F6', fontSize: '0.82rem' }}><strong>{stats.onboarding_drip.total_sent}</strong> total</span>
                <span style={{ color: '#6B7280', fontSize: '0.82rem' }}>{stats.onboarding_drip.today} today</span>
              </div>
              {Object.keys(stats.onboarding_drip.by_day).length > 0 && (
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.4rem' }}>
                  {Object.entries(stats.onboarding_drip.by_day).map(([day, count]) => (
                    <span key={day} style={{ fontSize: '0.65rem', padding: '0.1rem 0.35rem', borderRadius: '4px', background: 'rgba(59,130,246,0.1)', color: '#60A5FA' }}>{day}: {count}</span>
                  ))}
                </div>
              )}
            </div>

            {/* Conversion Drip */}
            <div style={{ ...cardStyle, textAlign: 'left', border: '1px solid rgba(16,185,129,0.2)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <TrendingUp size={18} color="#10B981" />
                  <span style={{ color: '#6EE7B7', fontWeight: 700, fontSize: '0.88rem' }}>Conversion Drip</span>
                </div>
                <button onClick={() => triggerCampaign('conversion')} style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981', padding: '0.3rem 0.6rem', borderRadius: '4px', border: 'none', cursor: 'pointer', fontSize: '0.72rem' }}>Run Now</button>
              </div>
              <p style={{ color: '#9CA3AF', fontSize: '0.78rem' }}>Day 2, 5, 8, 12, 18 — non-paying users</p>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                <span style={{ color: '#10B981', fontSize: '0.82rem' }}><strong>{stats.conversion_drip.total_sent}</strong> total</span>
                <span style={{ color: '#6B7280', fontSize: '0.82rem' }}>{stats.conversion_drip.today} today</span>
              </div>
            </div>

            {/* WhatsApp */}
            <div style={{ ...cardStyle, textAlign: 'left', border: '1px solid rgba(37,211,102,0.2)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <MessageCircle size={18} color="#25D366" />
                  <span style={{ color: '#25D366', fontWeight: 700, fontSize: '0.88rem' }}>WhatsApp Drip</span>
                </div>
                <button onClick={() => triggerCampaign('whatsapp')} style={{ background: 'rgba(37,211,102,0.15)', color: '#25D366', padding: '0.3rem 0.6rem', borderRadius: '4px', border: 'none', cursor: 'pointer', fontSize: '0.72rem' }}>Run Now</button>
              </div>
              <p style={{ color: '#9CA3AF', fontSize: '0.78rem' }}>WATI — welcome + drip (pending Meta approval)</p>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                <span style={{ color: '#25D366', fontSize: '0.82rem' }}><strong>{stats.whatsapp.total_sent}</strong> sent</span>
                <span style={{ color: '#6B7280', fontSize: '0.82rem' }}>{stats.whatsapp.successful} delivered</span>
              </div>
            </div>
          </div>

          {/* Send Test Email */}
          <div style={{ background: '#101632', borderRadius: '12px', padding: '1.25rem', border: '1px solid rgba(201,169,97,0.15)' }}>
            <p style={{ color: gold, fontWeight: 700, fontSize: '0.88rem', marginBottom: '0.75rem' }}>Send Test Email</p>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 0.5fr auto', gap: '0.5rem', alignItems: 'end' }}>
              <div>
                <label style={{ color: '#6B7280', fontSize: '0.72rem', display: 'block', marginBottom: '0.2rem' }}>Email</label>
                <input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="test@email.com"
                  style={{ background: '#0A0F2A', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', color: '#E5E7EB', padding: '0.5rem', width: '100%', fontSize: '0.85rem' }} />
              </div>
              <div>
                <label style={{ color: '#6B7280', fontSize: '0.72rem', display: 'block', marginBottom: '0.2rem' }}>Campaign</label>
                <select value={testType} onChange={e => setTestType(e.target.value)}
                  style={{ background: '#0A0F2A', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', color: '#E5E7EB', padding: '0.5rem', width: '100%', fontSize: '0.85rem' }}>
                  <option value="daily_insight">Daily Insight</option>
                  <option value="onboarding">Onboarding</option>
                  <option value="conversion">Conversion</option>
                </select>
              </div>
              <div>
                <label style={{ color: '#6B7280', fontSize: '0.72rem', display: 'block', marginBottom: '0.2rem' }}>Day</label>
                <input type="number" value={testDay} onChange={e => setTestDay(parseInt(e.target.value))} min={1} max={30}
                  style={{ background: '#0A0F2A', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '6px', color: '#E5E7EB', padding: '0.5rem', width: '100%', fontSize: '0.85rem' }} />
              </div>
              <button onClick={sendTest} disabled={sending || !testEmail}
                style={{ background: gold, color: '#0A0F2A', padding: '0.5rem 1rem', borderRadius: '6px', fontWeight: 700, border: 'none', cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                <Send size={14} style={{ display: 'inline', marginRight: 4 }} />{sending ? '...' : 'Send'}
              </button>
            </div>
          </div>

          {/* Recent Activity */}
          {stats.recent_activity?.length > 0 && (
            <div>
              <p style={{ color: '#9CA3AF', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>Recent Activity</p>
              <div style={{ display: 'grid', gap: '0.3rem' }}>
                {stats.recent_activity.map((a, i) => {
                  const typeColor = a.type === 'daily_insight' ? '#F59E0B' : a.type === 'onboarding' ? '#3B82F6' : '#10B981';
                  return (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.75rem', background: '#101632', borderRadius: '6px', borderLeft: `3px solid ${typeColor}` }}>
                      <div>
                        <span style={{ color: typeColor, fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase' }}>{a.type.replace('_', ' ')}</span>
                        <span style={{ color: '#6B7280', fontSize: '0.72rem', marginLeft: '0.5rem' }}>{a.email}</span>
                      </div>
                      <span style={{ color: '#4B5563', fontSize: '0.7rem' }}>{a.sent}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
